import {useCallback, useEffect, useRef, useState} from 'react';
import {useDisclosure, useToast} from '@chakra-ui/react';
import {v4 as uuidv4} from 'uuid';
import {patientService} from '../../services/patientService';
import {buildUnifiedPatientList} from '../../services/ehrService';
import {dateInputToLocalDate} from '../../constants/designTokens';

export const EMPTY_PATIENT_FORM_DATA = Object.freeze({
  firstName: '',
  lastName: '',
  preferredLanguage: '',
  preferredDialect: '',
  mrn: '',
  notes: '',
  gender: '',
  phoneNumber: '',
  email: '',
  dateOfBirth: '',
});

export const localPatientToPatientItem = (patient: any) =>
  buildUnifiedPatientList(patient ? [patient] : [], [])[0] || null;

const patientCreatePayloadFromForm = (formData: any, userData: any) => ({
  firstName: formData.firstName.trim(),
  lastName: formData.lastName.trim(),
  preferredLanguage: formData.preferredLanguage || null,
  preferredDialect: formData.preferredDialect || null,
  mrn: formData.mrn.trim() || null,
  notes: formData.notes.trim() || null,
  gender: formData.gender || null,
  phoneNumber: formData.phoneNumber.trim() || null,
  email: formData.email.trim() || null,
  dateOfBirth: dateInputToLocalDate(formData.dateOfBirth),
  createdBy: userData.userId,
  companyId: userData.companyId || null,
  facilityId: userData.facilityId,
});

export default function usePatientCreate({
  userData,
  onCreated,
  onUseExisting,
}: Record<string, any> = {}) {
  const toast = useToast();
  const {
    isOpen: isCreateModalOpen,
    onOpen: openModalDisclosure,
    onClose: closeCreateModal,
  } = useDisclosure();
  const [formData, setFormData] = useState({...EMPTY_PATIENT_FORM_DATA});
  const [saving, setSaving] = useState(false);
  // The patient the pre-create roster scan flagged as a likely duplicate
  // ({patient, patientItem}); the modal renders it as a warning with
  // "use existing" / "create anyway".
  const [duplicateMatch, setDuplicateMatch] = useState<any>(null);
  // Client-minted Patient doc id, one per modal open. Every submit/retry of
  // this form instance upserts the SAME doc server-side, so a double-click or
  // an error-then-retry can't mint twins (prod audit 2026-07-21: duplicate
  // pairs 18–64s apart). Re-minted on each open — never reused across opens,
  // so a stale id can't overwrite a patient created from a previous open.
  const pendingPatientIdRef = useRef<string | null>(null);
  // Re-entry guard: `saving` state lags a render behind a fast double-click.
  const submitInFlightRef = useRef(false);

  const openCreateModal = useCallback(() => {
    pendingPatientIdRef.current = uuidv4();
    setDuplicateMatch(null);
    openModalDisclosure();
  }, [openModalDisclosure]);

  // Any edit invalidates a shown duplicate warning; the next submit re-checks.
  useEffect(() => {
    setDuplicateMatch(null);
  }, [formData]);

  const resetFormData = useCallback(() => {
    setFormData({...EMPTY_PATIENT_FORM_DATA});
  }, []);

  const submit = useCallback(
    async ({skipDuplicateCheck = false} = {}) => {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        toast({
          title: 'First and last name are required',
          status: 'warning',
          duration: 2000,
        });
        return null;
      }
      if (submitInFlightRef.current) return null;

      submitInFlightRef.current = true;
      setSaving(true);
      try {
        if (!pendingPatientIdRef.current) {
          pendingPatientIdRef.current = uuidv4();
        }
        const pendingId = pendingPatientIdRef.current;

        if (!skipDuplicateCheck) {
          try {
            const existing = await patientService.findDuplicatePatientByName({
              firstName: formData.firstName,
              lastName: formData.lastName,
              dateOfBirth: formData.dateOfBirth || null,
              // A retry of a create that already landed finds its own doc by
              // name; that's a re-submit (same id upserts), not a duplicate.
              excludePatientId: pendingId,
            });
            if (existing) {
              setDuplicateMatch({
                patient: existing,
                patientItem: localPatientToPatientItem(existing),
              });
              return null;
            }
          } catch (err) {
            // The scan is a warning layer, not a gate — never block creation
            // on its availability (the stable id still covers re-submits).
            console.warn(
              'Duplicate-patient check failed; continuing with create:',
              (err as any)?.message,
            );
          }
        }

        const created = await patientService.createPatient({
          ...patientCreatePayloadFromForm(formData, userData),
          id: pendingId,
        });
        const patientItem = localPatientToPatientItem(created);
        pendingPatientIdRef.current = null;
        setDuplicateMatch(null);
        onCreated?.(created, patientItem);
        closeCreateModal();
        toast({title: 'Patient created', status: 'success', duration: 2000});
        resetFormData();
        return created;
      } catch (err) {
        // Keep pendingPatientIdRef: a retry after this error must upsert the
        // same doc in case the failed call actually landed server-side.
        console.error('Error creating patient:', err);
        toast({
          title: 'Failed to create patient',
          status: 'error',
          duration: 3000,
        });
        return null;
      } finally {
        submitInFlightRef.current = false;
        setSaving(false);
      }
    },
    [closeCreateModal, formData, onCreated, resetFormData, toast, userData],
  );

  const handleCreate = useCallback(() => submit(), [submit]);

  // "Create anyway" on the duplicate warning.
  const confirmDuplicateCreate = useCallback(
    () => submit({skipDuplicateCheck: true}),
    [submit],
  );

  // "Use existing" on the duplicate warning: hand the matched patient to the
  // caller (same select-the-patient outcome as a create) without creating.
  const chooseExistingPatient = useCallback(() => {
    const match = duplicateMatch;
    if (!match?.patient) return;
    pendingPatientIdRef.current = null;
    setDuplicateMatch(null);
    onUseExisting?.(match.patient, match.patientItem);
    closeCreateModal();
    resetFormData();
  }, [closeCreateModal, duplicateMatch, onUseExisting, resetFormData]);

  return {
    isCreateModalOpen,
    openCreateModal,
    closeCreateModal,
    formData,
    setFormData,
    saving,
    handleCreate,
    duplicateMatch,
    confirmDuplicateCreate,
    chooseExistingPatient,
  };
}
